import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { checkMot } from '../../../../ressources/functionJS/checkMot';
import * as type_btn from '../../../../ressources/glossaires/glossaire_type_de_bouton';
import * as s from '../../../../ressources/StyleGlobal/StyleGlobal';
import Bouton from '../../../UI/Bouton/Bouton';
import calendrier from './calendrier.png';
import styles from './FormulaireDateDeCouche.module.scss';

const affichagePetit = '17% 17% 17% 17%';
const affichageGrand = '0% 0% 0% 0%';

const FormulaireDateDeCouche = ({
  width,
  afficherExpand: initialAfficherExpand,
  historique_couches,
  controlLayers,
  map_vue,
  ajoutLayer,
  ajout_avancement_tunnelier,
  hideHeader,
  headerPopupText,
  popup_id,
  eventAuCliqueFermeture,
  eventAuCliqueOuverture,
  children,
}) => {
  // State hooks
  const [dateInit, setDateInit] = useState('2023-03-31 00:00:00');
  const [dateFin, setDateFin] = useState('2023-03-31 17:00:00');
  const [initialisationFormulaire, setInitialisationFormulaire] =
    useState(true);
  const [dicoCouche, setDicoCouche] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [overflowX, setOverflowX] = useState('auto');
  const [overflowY, setOverflowY] = useState('auto');
  const [inset, setInset] = useState(
    initialAfficherExpand ? affichageGrand : affichagePetit
  );
  const [afficherExpand, setAfficherExpand] = useState(
    initialAfficherExpand || false
  );

  // Handle width changes
  useEffect(() => {
    if (width === '100%') {
      setInset('5.9% 0.1% 0% 0%');
    } else {
      setInset(affichagePetit);
    }
  }, [width]);

  // Handle historique_couches changes
  useEffect(() => {
    if (initialisationFormulaire && historique_couches) {
      const newDicoCouche = {};

      for (let couche of historique_couches) {
        if (couche?.properties?.name === 'avancement') {
          newDicoCouche[couche.name] = couche;
        } else if (
          couche?.parametre?.layers &&
          (couche.parametre.layers.startsWith('ISOTASS') ||
            couche.parametre.layers.startsWith('MNDZ'))
        ) {
          const path = couche.parametre.map.split('\\');
          const nom_dossier = path[path.length - 2];

          if (!(nom_dossier in newDicoCouche)) {
            newDicoCouche[nom_dossier] = [couche];
          } else {
            newDicoCouche[nom_dossier] = [
              ...newDicoCouche[nom_dossier],
              couche,
            ];
          }
        }
      }

      setDicoCouche(newDicoCouche);
    }
  }, [historique_couches, initialisationFormulaire]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    if (typeof eventAuCliqueFermeture === 'function') {
      eventAuCliqueFermeture();
    }
  }, [eventAuCliqueFermeture]);

  const handleOpenModal = useCallback(() => {
    setShowModal(true);
    if (typeof eventAuCliqueOuverture === 'function') {
      eventAuCliqueOuverture();
    }
  }, [eventAuCliqueOuverture]);

  const handleExpand = useCallback(() => {
    setInset('0% 0% 0% 0%');
    setAfficherExpand(true);
    setOverflowX('hidden');

    const element = document.getElementById(popup_id);
    if (element) {
      element.requestFullscreen();
    }
  }, [popup_id]);

  const handleShrink = useCallback(() => {
    setInset(affichagePetit);
    setAfficherExpand(false);
    setOverflowX('auto');
    setOverflowY('auto');

    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    for (const keyName of Object.keys(dicoCouche)) {
      if (Array.isArray(dicoCouche[keyName])) {
        try {
          const response = await fetch(
            'https://qgis.argos-v19.symf.itmsol.fr/qgis/isolignes_historique/iso_histo.php',
            {
              method: 'POST',
              mode: 'cors',
              cache: 'no-cache',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
              },
              redirect: 'follow',
              referrerPolicy: 'no-referrer',
              body: JSON.stringify({
                projectName: keyName,
                ti: dateInit,
                tf: dateFin,
              }),
            }
          );

          const data = await response.json();

          if (data.state === 'OK') {
            setInitialisationFormulaire(false);
          }

          // Handle layer updates
          Object.keys(data).forEach((key) => {
            // Remove existing layers
            dicoCouche[keyName].forEach((layer) => {
              const layerToRemove = controlLayers._layers.find(
                (obj) => obj.name === layer.layer_historique_name
              );

              if (layer.layer_historique_name && layerToRemove) {
                map_vue.removeLayer(layerToRemove.layer);
                controlLayers.removeLayer(layerToRemove.layer);
                map_vue.invalidateSize();
              }
            });

            // Add new layers
            if (key === 'mndz') {
              ajoutLayer({
                ...data[key],
                name: data[key].parametre.layers,
                position: 9,
              });
            } else if (key === 'isotass') {
              ajoutLayer({
                ...data[key],
                name: data[key].parametre.layers,
                position: 10,
              });
            }
          });
        } catch (error) {
          console.error('Error processing layers:', error);
        }
      } else {
        // Handle tunnelier trace
        const date_uts_fin = new Date(dateFin).valueOf() / 1000;
        let ind_max_uts = 0;

        while (
          ind_max_uts < dicoCouche[keyName].geometry.t.length &&
          dicoCouche[keyName].geometry.t[ind_max_uts] < date_uts_fin
        ) {
          ind_max_uts++;
        }

        const geojson = {
          ...dicoCouche[keyName],
          geometry: {
            type: dicoCouche[keyName].geometry.type,
            coordinates: dicoCouche[keyName].geometry.coordinates.slice(
              0,
              ind_max_uts
            ),
            t: dicoCouche[keyName].geometry.t.slice(0, ind_max_uts),
          },
        };

        const layerToRemove = controlLayers._layers.find(
          (obj) => obj.name === keyName
        );

        if (keyName && layerToRemove) {
          map_vue.removeLayer(layerToRemove.layer);
          controlLayers.removeLayer(layerToRemove.layer);
          map_vue.invalidateSize();
        }

        ajout_avancement_tunnelier(geojson);
      }
    }
  }, [
    dicoCouche,
    dateInit,
    dateFin,
    controlLayers,
    map_vue,
    ajoutLayer,
    ajout_avancement_tunnelier,
  ]);

  // Render header and expand button
  const renderHeader = () => {
    let Textheader;
    let colorBg = [s.text_right, s.col_1];
    let classpopup = [s.text_right];

    if (!hideHeader) {
      classpopup.push([s.card_header, styles.styleB].join(' '));
      Textheader = (
        <span className={[styles.headerText, s.col_11].join(' ')}>
          {headerPopupText}
        </span>
      );
    } else {
      colorBg.push(styles.headerTextD);
      Textheader = (
        <span className={[styles.headerTextD, s.col_11].join(' ')}>
          {headerPopupText}
        </span>
      );
    }

    const expandButton = afficherExpand ? (
      <Bouton
        ajoutClass={[styles.PositionBoutonFermeture, styles.BoutonOutil].join(
          ' '
        )}
        type={type_btn.Min}
        rond={true}
        cliquer={handleShrink}
      />
    ) : (
      <Bouton
        ajoutClass={[styles.PositionBoutonFermeture, styles.BoutonOutil].join(
          ' '
        )}
        type={type_btn.Max}
        rond={true}
        cliquer={handleExpand}
      />
    );

    return { Textheader, colorBg, classpopup, expandButton };
  };

  const { Textheader, colorBg, classpopup, expandButton } = renderHeader();

  return (
    <div className={styles.divMaster}>
      <img
        title={checkMot('historique_couches')}
        src={calendrier}
        style={{ width: '45px', padding: '12px', cursor: 'pointer' }}
        onClick={handleOpenModal}
        alt={checkMot('calendrier')}
      />
      <Modal
        id={popup_id}
        isOpen={showModal}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            position: 'absolute',
            zIndex: '1000',
            border: 'black 1px solid',
            backgroundColor: 'transparent',
            transition: 'width 2s, height 4s',
          },
          content: {
            overflowX,
            overflowY,
            inset,
            padding: '0px',
            boxShadow: '-5px 0px 5px rgba(0, 0, 0, 0.226)',
          },
        }}
      >
        <div className={classpopup.join(' ')}>
          {Textheader}
          <span className={colorBg.join(' ')}>
            {expandButton}
            <Bouton
              ajoutClass={styles.PositionBoutonFermeture}
              type={type_btn.fermer}
              rond={true}
              cliquer={handleCloseModal}
            />
          </span>
        </div>

        <div
          className={[
            s.d_flex,
            s.justify_content_sm_around,
            styles.ConteneurHorizontal,
          ].join(' ')}
        >
          <div>
            {children}
            <div style={{ padding: '5px' }}>
              <h5>{checkMot('couche_avec_historique')}</h5>
              {Object.entries(dicoCouche).map(([keyName, value]) =>
                Array.isArray(value) ? (
                  <ul key={keyName}>
                    {keyName}
                    {value.map((couche) => (
                      <li key={couche.name} className={styles.IndentationLi}>
                        {couche.name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul key={keyName}>{keyName}</ul>
                )
              )}
            </div>
          </div>
          <div>
            <h5>{checkMot('selectionner_une_plage_de_temps')}</h5>
            <input
              value={dateInit}
              onChange={(e) => setDateInit(e.target.value)}
            />
            <input
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
            />
            <Bouton cliquer={handleSubmit}>Envoie</Bouton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FormulaireDateDeCouche;
